import "../../../../extenstions/stringExtenstions";
import React, { useEffect, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "ui-components/button/button";
import XGSFormDate from "ui-components/form/date/xgsFormDate";
import XGSFormTextarea from "ui-components/form/textarea/xgsFormTextarea";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import PickupService from "app/data/pickup/pickupService";
import {
  PickupRescheduleModel,
  PickupRescheduleSchema,
  PICKUP_REASON_OPTIONS,
} from "app/data/pickup/models";
import { currentOrClosestBusinessDay } from "hooks/utils";
import { fromTimeOptions, toTimeOptions } from "services/common/time";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import "../../../common/pickup-details/pickupDetails.scss";
import "./bulkUnassignedRescheduleModal.scss";

const pickupService = PickupService.getInstance();

const initialValues: PickupRescheduleModel = {
  pickupDate: "",
  pickupReadyTime: "",
  pickupCloseTime: "",
  reason: "",
  notes: "",
};

export type BulkRescheduleProgress =
  | { phase: "idle" }
  | { phase: "running"; completed: number; total: number }
  | {
      phase: "done";
      completed: number;
      total: number;
      failedIds: string[];
    };

export interface BulkUnassignedRescheduleModalProps {
  open: boolean;
  pickupIds: string[];
  onClose: () => void;
  /** Called after any successful API writes so lists can refetch. */
  onRefresh: () => void;
  /** Called only when every selected pickup rescheduled successfully. */
  onAllSucceeded?: () => void;
}

/** Matches V1 `PickupReschedule` → `reschedulePickup` payload: API date + times as `h:mm a` (e.g. `6:00 am`). */
function buildRescheduleRequestPayload(data: PickupRescheduleModel): PickupRescheduleModel {
  const request = { ...data };
  request.pickupDate = String(request.pickupDate ?? "").toApiDateFormat();
  request.pickupReadyTime = data.pickupReadyTime;
  request.pickupCloseTime = data.pickupCloseTime;
  return request;
}

export const BulkUnassignedRescheduleModal: React.FC<BulkUnassignedRescheduleModalProps> = ({
  open,
  pickupIds,
  onClose,
  onRefresh,
  onAllSucceeded,
}) => {
  const formRef = useRef<FormikProps<PickupRescheduleModel>>(null);
  const [pickupDateFormValue, setPickupDateFormValue] = useState<string | undefined>();
  const [pickupReadyTimeFormValue, setPickupReadyTimeFormValue] = useState<XGSSelectOption | null>();
  const [pickupCloseTimeFormValue, setPickupCloseTimeFormValue] = useState<XGSSelectOption | null>();
  const [progress, setProgress] = useState<BulkRescheduleProgress>({ phase: "idle" });

  useEffect(() => {
    if (!open) return;
    setProgress({ phase: "idle" });
    formRef.current?.resetForm();
    setPickupDateFormValue(undefined);
    setPickupReadyTimeFormValue(null);
    setPickupCloseTimeFormValue(null);
    formRef.current?.setFieldValue("reason", "");
  }, [open]);

  useEffect(() => {
    if (!pickupCloseTimeFormValue || !pickupReadyTimeFormValue) return;
    const options = toTimeOptions(pickupReadyTimeFormValue.value);
    const toIndex = options.findIndex((obj) => obj.value === pickupCloseTimeFormValue?.value);
    if (toIndex === -1) {
      setPickupCloseTimeFormValue(options[0]);
      formRef.current?.setFieldValue("pickupCloseTime", options[0].value);
    }
  }, [pickupCloseTimeFormValue, pickupReadyTimeFormValue]);

  const onPickupDateChange = (date: string) => {
    formRef.current?.setFieldValue("pickupDate", date || "");
    setPickupDateFormValue(date);
  };

  const runBulk = async (data: PickupRescheduleModel) => {
    const total = pickupIds.length;
    if (total === 0) return;

    const payload = buildRescheduleRequestPayload(data);

    setProgress({ phase: "running", completed: 0, total });
    const failedIds: string[] = [];

    for (let i = 0; i < pickupIds.length; i++) {
      const id = pickupIds[i];
      const response = await pickupService.reschedulePickup(id, payload);
      if (!response.ok()) {
        failedIds.push(id);
      }
      setProgress({ phase: "running", completed: i + 1, total });
    }

    setProgress({ phase: "done", completed: total - failedIds.length, total, failedIds });

    if (failedIds.length === 0) {
      toast.success(
        total === 1 ? "Pickup rescheduled." : `Rescheduled ${total} pickups.`,
        { autoClose: 3000 }
      );
      onRefresh();
      onAllSucceeded?.();
      onClose();
    } else if (failedIds.length === total) {
      toast.error("Could not reschedule the selected pickups.", { autoClose: 4000 });
      onRefresh();
    } else {
      toast.warn(
        `Rescheduled ${total - failedIds.length} of ${total} pickups. ${failedIds.length} failed.`,
        { autoClose: 5000 }
      );
      onRefresh();
    }
  };

  const loadingProgress =
    progress.phase === "running" && progress.total > 0
      ? Math.floor((progress.completed / progress.total) * 100)
      : 0;

  return (
    <SlideOutSidebar
      header="Reschedule pickups"
      show={open}
      size="extrasmall"
      onClose={onClose}
      className="xgs-pickup-details__slideout xgs-bulk-unassigned-reschedule"
    >
      <div className="xgs-pickup-details__slideout__inner-header">
        Reschedule {pickupIds.length === 1 ? "pickup" : `${pickupIds.length} pickups`}
      </div>
      <div className="xgs-pickup-details__slideout__text">
        Enter one new pickup window. It will be applied to all selected pickups.
      </div>
      {progress.phase === "running" ? (
        <div className="xgs-bulk-unassigned-reschedule__progress">
          <div className="xgs-bulk-unassigned-reschedule__progress-info">
            <span className="xgs-bulk-unassigned-reschedule__progress-pct">{loadingProgress}%</span>
            <span className="xgs-bulk-unassigned-reschedule__progress-label">
              {progress.completed}/{progress.total} completed
            </span>
          </div>
          <div className="xgs-bulk-unassigned-reschedule__progress-bar">
            <div style={{ width: `${loadingProgress}%` }} />
          </div>
        </div>
      ) : null}
      {progress.phase === "done" && progress.failedIds.length > 0 ? (
        <div className="xgs-bulk-unassigned-reschedule__failed" role="alert">
          <div className="xgs-bulk-unassigned-reschedule__failed-title">
            {progress.failedIds.length} pickup{progress.failedIds.length === 1 ? "" : "s"} could not
            be rescheduled.
          </div>
        </div>
      ) : null}
      <div className="xgs-pickup-details__reschedule-form">
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={PickupRescheduleSchema}
          enableReinitialize
          onSubmit={(values) => {
            if (progress.phase === "running") return;
            void runBulk(values);
          }}
        >
          {(formik: FormikProps<PickupRescheduleModel>) => (
            <Form>
              <LabeledSelectInput
                name="reason"
                label="Reason:"
                isSearchable={false}
                labelMode={LabelModes.column}
                onValueChange={(v) => {
                  formik.setFieldValue("reason", v?.value || "");
                }}
                options={PICKUP_REASON_OPTIONS}
                required={true}
                formik={true}
                placeholder="Select reason..."
                menuPlacement="bottom"
                value={
                  PICKUP_REASON_OPTIONS.find((option) => option.value === formik.values.reason) ||
                  null
                }
                className="xgs-pickup-details__reason-dropdown"
                disabled={progress.phase === "running"}
              />
              <XGSFormTextarea
                name="notes"
                label="Notes:"
                required={false}
                rows={3}
                counter={255}
                className="xgs-pickup-details__cancel__notes"
                disabled={progress.phase === "running"}
              />
              <div className="xgs-pickup__date">
                <XGSFormDate
                  name="pickupDate"
                  label="New Pickup Date:"
                  placeholder="Select date..."
                  onDateChange={onPickupDateChange}
                  value={pickupDateFormValue}
                  onChange={() => null}
                  minDate={new Date(currentOrClosestBusinessDay())}
                  required={true}
                  labelMode={LabelModes.column}
                  disableWeekends={true}
                  disabledWeekDays={{}}
                  disabledDates={[]}
                  disabled={progress.phase === "running"}
                />
              </div>
              <div className="xgs-pickup__time xgs-bol__return__availability">
                <div className="xgs-bol__return__availability__label">
                  Hours of Availability: <span>*</span>
                </div>
                <div className="xgs-bol__return__availability__controls">
                  <LabeledSelectInput
                    name="pickupReadyTime"
                    value={pickupReadyTimeFormValue}
                    label=""
                    isSearchable={false}
                    labelMode={LabelModes.column}
                    onValueChange={(v) => {
                      formik.setFieldValue("pickupReadyTime", v?.value);
                      setPickupReadyTimeFormValue(v);
                    }}
                    options={fromTimeOptions()}
                    required={true}
                    requiredAsteriskDisabled={true}
                    formik={true}
                    placeholder="Ready..."
                    menuPlacement="bottom"
                    disabled={progress.phase === "running"}
                  />
                  <div className="xgs-bol__return__availability__controls__delimiter">-</div>
                  <LabeledSelectInput
                    name="pickupCloseTime"
                    value={pickupCloseTimeFormValue}
                    label=""
                    isSearchable={false}
                    labelMode={LabelModes.column}
                    onValueChange={(v) => {
                      formik.setFieldValue("pickupCloseTime", v?.value);
                      setPickupCloseTimeFormValue(v);
                    }}
                    options={
                      pickupReadyTimeFormValue ? toTimeOptions(pickupReadyTimeFormValue.value) : []
                    }
                    required={true}
                    requiredAsteriskDisabled={true}
                    formik={true}
                    placeholder="Close..."
                    menuPlacement="bottom"
                    disabled={progress.phase === "running"}
                  />
                </div>
              </div>
              <div className="xgs-pickup-details__slideout__buttons">
                <Button
                  theme={ButtonThemes.gray}
                  type="button"
                  onClick={onClose}
                  disabled={progress.phase === "running"}
                >
                  Cancel
                </Button>
                <Button
                  theme={ButtonThemes.blue}
                  type="submit"
                  disabled={
                    !formik.isValid ||
                    !formik.dirty ||
                    progress.phase === "running" ||
                    pickupIds.length === 0
                  }
                  spinner={progress.phase === "running"}
                >
                  {progress.phase === "running" ? "Rescheduling…" : "Reschedule all"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </SlideOutSidebar>
  );
};
